import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Cross-page active site + blueprint selection.
 *
 * Why a store instead of per-page `useState`: the digital twin, evacuation
 * planner, blueprints manager and dashboard each used to hold their own
 * `pickedSite` / `pickedBp` state, so choosing a **non-primary** floor plan on
 * one screen never carried over to the others — the operator re-picked the same
 * site/blueprint on every route. Lifting the selection into one zustand store
 * (same pattern as `widgets/twin3d/*Store.ts`) makes a single pick propagate
 * across the whole app.
 *
 * `siteId` is persisted (localStorage) since 2026-09-08: an operator who picked
 * 금산 once should land on 금산 after the next login too — the 관제 board opened
 * on the first-ranked site every time, which read as "금산 도면이 없다". The
 * blueprint pick stays session-scoped (it belongs to a page's floor choice).
 * `null` still means "no explicit pick yet" so callers keep their existing
 * `?? sites[0]?.id` / primary-blueprint fallbacks.
 *
 * `defaultSiteId` (2026-09-22, 「기본 사이트 설정과 화면 보기 선택 기능 있어야지」):
 * 현장 둘을 맡은 관리자가 보드를 **어느 현장으로 열지** 정해 두는 값. 마지막 선택
 * 유지만으로는 부족했다 — 다른 현장을 잠깐 보고 나가면 다음 로그인이 그 현장으로
 * 열리고, 옮기는 수단(오른쪽 열 타일)은 눈에 띄지 않아 「바꾸는 법을 못 찾겠다」가 됐다.
 *
 *   불러오기(rehydrate) 순서 — `merge` 에서 정한다:
 *     1. 저장된 `defaultSiteId` 가 있으면 `siteId := defaultSiteId`
 *        → 로그인·새로고침마다 이 store 를 읽는 모든 화면(관제·트윈·대피)이 기본 현장으로 연다.
 *     2. 없으면(null) 저장된 마지막 `siteId` 그대로 — 예전 동작 유지.
 *   세션 중의 선택(`setSite`)은 여태처럼 `siteId` 만 바꾼다. 기본값은 다음 불러오기에만
 *   작용하므로, 잠깐 다른 현장을 봐도 기본 현장이 바뀌지 않는다.
 */
interface ActiveSiteState {
  /** Explicitly selected site id, or null to defer to the page's default. */
  siteId: number | null;
  /** Explicitly selected blueprint id, or null to defer to the primary. */
  blueprintId: number | null;
  /** 로그인·새로고침 뒤 처음 여는 현장. null = 정하지 않음(마지막 선택 유지). */
  defaultSiteId: number | null;
  /** Select a site. Clears the blueprint — it belongs to the previous site. */
  setSite: (siteId: number | null) => void;
  setBlueprint: (blueprintId: number | null) => void;
  /** 기본 현장을 정하거나(null) 푼다. 현재 선택은 건드리지 않는다 — 호출자가 필요하면 `setSite` 도 부른다. */
  setDefaultSite: (siteId: number | null) => void;
  /**
   * 현장 목록이 오면 **목록에 없는** 저장값을 지운다(`siteId`·`defaultSiteId`).
   * 저장은 브라우저 단위라 같은 PC 에 다른 조직 계정이 들어오거나 현장이 삭제·비활성되면
   * 남의 id 가 남는다 — 예전엔 첫 선택이 덮어써 자연히 나았지만 기본값은 불러올 때마다
   * 다시 주입되어(`merge`) 트윈·대피가 매번 데모 건물을 열었다. ⛔ 빈 목록(로딩·재조회
   * 순간)에는 아무것도 안 지운다 — 관제 보드의 「빈 순간에 선택을 버리지 않는다」와 같은 이유.
   */
  pruneMissing: (siteIds: readonly number[]) => void;
}

type PersistedSlice = Pick<ActiveSiteState, "siteId" | "defaultSiteId">;

const STORAGE_KEY = "expertgas.activeSite.v1";

export const useActiveSiteStore = create<ActiveSiteState>()(
  persist(
    (set, get) => ({
      siteId: null,
      blueprintId: null,
      defaultSiteId: null,
      setSite: (siteId) => set({ siteId, blueprintId: null }),
      setBlueprint: (blueprintId) => set({ blueprintId }),
      setDefaultSite: (defaultSiteId) => set({ defaultSiteId }),
      pruneMissing: (siteIds) => {
        if (siteIds.length === 0) return;
        const s = get();
        const has = (id: number | null) => id != null && siteIds.includes(id);
        const next: Partial<ActiveSiteState> = {};
        if (s.defaultSiteId != null && !has(s.defaultSiteId)) next.defaultSiteId = null;
        // 현장을 버리면 도면도 버린다 — 그 현장의 것이다(`setSite` 와 같은 규칙).
        if (s.siteId != null && !has(s.siteId)) { next.siteId = null; next.blueprintId = null; }
        // 지울 게 없으면 set 도 안 한다 — 빈 set 도 persist 가 저장소를 다시 쓴다(다른 탭에 storage 이벤트).
        if (Object.keys(next).length > 0) set(next);
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (s): PersistedSlice => ({ siteId: s.siteId, defaultSiteId: s.defaultSiteId }),
      // localStorage 는 동기라 첫 렌더 전에 합쳐진다 — 관제 보드가 `siteId` 를 useState 초기값으로
      // 한 번만 읽어도 기본 현장이 반영된다. 예전 저장분(defaultSiteId 없음)은 null 로 읽혀 2번 경로.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<PersistedSlice>;
        const defaultSiteId = p.defaultSiteId ?? null;
        return {
          ...current,
          defaultSiteId,
          siteId: defaultSiteId ?? p.siteId ?? null,
        };
      },
    },
  ),
);

/**
 * 다른 탭이 정한 기본 현장을 이 탭에도 반영한다.
 *
 * persist 는 매 set 마다 **이 탭 메모리**의 slice 를 통째로 쓴다. 관제를 한 탭, 트윈을 다른
 * 탭에 띄운 관제석에서 한쪽이 별표를 켜도, 다른 탭이 타일 하나만 눌러 `setSite` 하면
 * 그 탭 메모리의 `defaultSiteId: null` 이 저장소를 덮어 별표가 소리 없이 풀렸다.
 * `defaultSiteId` 만 받는다 — `rehydrate()` 는 `merge` 를 다시 타 이 탭이 보고 있던
 * `siteId` 까지 남의 값으로 바꿔 버린다. 값이 지워진 이벤트(newValue null)는 무시.
 */
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== STORAGE_KEY || e.newValue == null) return;
    try {
      const raw = (JSON.parse(e.newValue) as { state?: Partial<PersistedSlice> }).state;
      const defaultSiteId = raw?.defaultSiteId ?? null;
      if (defaultSiteId !== useActiveSiteStore.getState().defaultSiteId) {
        useActiveSiteStore.setState({ defaultSiteId });
      }
    } catch {
      /* 남의 탭이 쓴 값이 깨져 있으면 이 탭 상태는 건드리지 않는다 */
    }
  });
}
